import { useQuery } from '@tanstack/react-query';
import { api } from '@pc/api/client';
import type { operations } from '@pc/api/schema';
import { ApiErrorNotice } from './ApiErrorNotice';
import { Badge, Card, Id } from './ui';

/**
 * Straight off the generated contract. `status` is the two-value enum
 * `ok | draining` and `inFlight` is a count; both are required.
 */
export type Health = operations['getHealth']['responses'][200]['content']['application/json'];

/*
 * Poll cadence.
 *
 * `GET /api/health` is the one unauthenticated, unmetered endpoint in the API —
 * everything else spends the 120/min-per-actor budget an operator needs for the
 * work itself — and it reports only local process state, checking no upstream.
 * So polling it is legitimate in a way polling a job is not: it costs the
 * operator nothing and the API almost nothing.
 *
 * Ten seconds is deliberately the same ceiling `lib/jobs.ts` eases a running
 * job's poll out to. This reading exists to explain what the job screen is
 * about to show, so it must not be staler than that screen; and there is
 * nothing to gain from being fresher than the fastest thing it can explain.
 */
const HEALTH_POLL_MS = 10_000;

/**
 * Whether the API is up, and whether it is on its way down.
 *
 * `draining` is not a curiosity: it is the exact condition that strands a job
 * as `abandoned`. The service is shutting down, provisioning already in flight
 * will not be resumed, and until this endpoint is asked nothing in the console
 * says so — a 503 on `/api/auth/me` replaces the whole app with an error that
 * cannot tell "the API is restarting" apart from "you are off the VPN".
 *
 * It lives in `components/` and is mounted in `routes/__root.tsx` beside
 * `<SessionGate>` rather than inside it, because the moment this is worth
 * reading is the moment there is no session to be had. On the home route it was
 * unreachable in exactly that case: the gate replaces the whole outlet when
 * `/api/auth/me` fails, so the one panel that needs no session was the one the
 * session took down with it.
 *
 * SILENCE MEANS `ok`. Sitting above every screen in the console, a healthy
 * reading has nothing to say and says nothing: no card, no badge, no heading
 * to scroll past on the way to the work. An operator who is shown "API: ok" on
 * every page for a month stops seeing that region of the screen, and stops
 * seeing it on the day it says `draining` too — which is the only day it
 * matters. So the panel is absent while things are well, and its mere
 * appearance is the first half of the signal.
 */
export function ApiHealth() {
  const health = useQuery({
    queryKey: ['health'],
    queryFn: async (): Promise<Health> => {
      const { data } = await api.GET('/api/health');
      return data as Health;
    },
    refetchInterval: HEALTH_POLL_MS,
    // A liveness reading is worthless the moment it is old: the shared 15s
    // stale window would let a return to this tab render a cached "ok" for a
    // service that has since begun draining. No `retry`/`retryDelay` override —
    // the shared policy in lib/query.ts already retries what is worth retrying.
    staleTime: 0,
  });

  /*
   * Nothing at all while the first read is outstanding. A "Loading API health…"
   * line would render above every screen on every cold load, which is furniture
   * announcing a question nobody asked — and the answer arrives in well under
   * the time it takes to read the announcement.
   */
  if (health.isPending) return null;

  if (health.isError) {
    /*
     * Not reaching the endpoint at all is its own answer, and a different one
     * from `draining`: it means the request never got there — off the VPN, DNS,
     * the whole service already gone — and the shared notice is what says so.
     * Nothing status-specific is branched on here.
     *
     * `<SessionGate>` below will usually be reporting its own failure at the
     * same time, since `/api/auth/me` cannot survive what this could not reach.
     * That is a small redundancy accepted on purpose: this panel has to stand
     * on its own, because the case it exists for is the one where everything
     * below it has failed.
     */
    return (
      <Card title="API">
        <ApiErrorNotice error={health.error} onRetry={() => void health.refetch()} />
      </Card>
    );
  }

  if (health.data.status !== 'draining') return null;

  return <Draining inFlight={health.data.inFlight} />;
}

function Draining({ inFlight }: { inFlight: number }) {
  return (
    <Card title="API">
      <p>
        {/*
          * Warn, not the failure red: a restart is planned work, not a fault.
          * The colour says "notice this"; the words below say what it costs.
          */}
        <Badge tone="warn">draining</Badge> <Badge tone="neutral">{inFlight} in flight</Badge>
      </p>

      <p role="alert">
        The service is restarting. A job running right now will be marked <Id>abandoned</Id> rather
        than resumed: the infrastructure it touches may be left half-built at the stage it reached,
        and it will <strong>not</strong> be resumed automatically, because re-running provisioning
        from there risks duplicating what already exists. Do not start provisioning or teardown
        until this panel disappears again, and open any job you already have in flight — check the
        target directly before touching it by hand. The count above is the requests the API is
        still finishing before it exits.
      </p>
    </Card>
  );
}
